import React, { useState } from 'react';
import { motion } from 'motion/react';
import {
  Sparkles,
  Target,
  DollarSign,
  Calendar,
  Layers,
  Search,
  Globe,
  Sliders,
  CheckCircle2,
  AlertCircle,
  Megaphone,
  Smartphone,
  Eye,
  Zap,
  ArrowRight,
  MapPin,
  Building,
  ShieldCheck,
  X,
  Play
} from 'lucide-react';
import { DEFAULT_NIGERIAN_SOLAR_KEYWORDS, createGoogleCampaign } from '../../../lib/marketing/googleAds';
import { NIGERIAN_META_TARGETING_PRESETS, createMetaCampaign } from '../../../lib/marketing/metaAds';

interface CampaignWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onCampaignCreated: () => void;
  preSelectedPreset?: string;
}

export const CampaignWizard: React.FC<CampaignWizardProps> = ({
  isOpen,
  onClose,
  onCampaignCreated,
  preSelectedPreset,
}) => {
  // Wizard State
  const [channel, setChannel] = useState<'GOOGLE_ADS' | 'META_ADS'>('GOOGLE_ADS');
  const [googleSubChannel, setGoogleSubChannel] = useState<'SEARCH' | 'PERFORMANCE_MAX'>('SEARCH');
  const [campaignName, setCampaignName] = useState('Umuahia Commercial Solar High-Intent Campaign');
  
  // Location & Targeting
  const [selectedRegion, setSelectedRegion] = useState('Umuahia, Abia State');
  const [radiusKm, setRadiusKm] = useState(25);
  const [dailyBudgetNgn, setDailyBudgetNgn] = useState(25000); // ₦25,000/day
  const [durationDays, setDurationDays] = useState(14); // 14 days
  
  // Ad Copy & Hooks
  const [selectedHookIndex, setSelectedHookIndex] = useState(0);
  const [headline, setHeadline] = useState('Beat Band A Tariffs in Umuahia — Switch to Access Tech Solar');
  const [primaryText, setPrimaryText] = useState('Stop Wasting ₦15,000/Day on Petrol. Get 24/7 Uninterrupted Pure Sine Solar & Lithium Battery Power with 25-Year Panel Warranty.');
  const [keywords, setKeywords] = useState<string[]>(DEFAULT_NIGERIAN_SOLAR_KEYWORDS.slice(0, 5));
  const [newKeywordInput, setNewKeywordInput] = useState('');
  
  // Meta Placements
  const [placements, setPlacements] = useState<('FACEBOOK_FEED' | 'INSTAGRAM_FEED' | 'INSTAGRAM_REELS')[]>([
    'FACEBOOK_FEED',
    'INSTAGRAM_FEED',
    'INSTAGRAM_REELS',
  ]);

  // Loading state
  const [isLaunching, setIsLaunching] = useState(false);
  const [launchSuccess, setLaunchSuccess] = useState(false);

  // High-converting Nigerian Ad Copy Hooks
  const AD_HOOK_TEMPLATES = [
    {
      title: 'Umuahia & Aba Commercial Hook',
      headline: 'Beat Band A Tariffs in Umuahia — Switch to Access Tech Solar',
      primaryText: 'Power your shop, freezer, or office 24/7 without burning ₦15,000/day on petrol. COREN certified installers in Abia State.',
    },
    {
      title: 'Lagos Band A & Fuel Saving Hook',
      headline: 'Stop Wasting ₦15,000/Day on Petrol — Switch to Access Tech Solar',
      primaryText: 'Beat the ₦225/kWh Band A tariff in Lekki, Ikeja & V.I. Complete 3.5kVA lithium systems installed in 24 hours with 25-yr warranty.',
    },
    {
      title: 'Port Harcourt Industrial & Oil Hook',
      headline: 'Zero Noise. Zero Fuel. 15-Year Life. Upgrade Your Business in Port Harcourt',
      primaryText: 'Commercial-grade 7.5kVA to 20kVA hybrid microgrids engineered for heavy loads, deep freezers, and industrial facilities in Rivers State.',
    },
    {
      title: 'Abuja Estate Outage Protection Hook',
      headline: 'Abuja Estate Power Outage Protection — Guaranteed 24-Hour Lithium Backup',
      primaryText: 'Seamless 0ms transfer keeps your lights, ACs, and security systems online during grid blackouts in Maitama, Gwarinpa & Asokoro.',
    },
  ];

  // ApplyPreset
  const handleApplyPreset = (presetId: string) => {
    const preset = NIGERIAN_META_TARGETING_PRESETS.find((p) => p.id === presetId);
    if (preset) {
      setSelectedRegion(preset.region);
      setDailyBudgetNgn(preset.recommendedBudgetNgn);
      setHeadline(preset.hook);
      setCampaignName(`${preset.name} Ad Campaign`);
    }
  };

  const handleSelectHook = (index: number) => {
    setSelectedHookIndex(index);
    setHeadline(AD_HOOK_TEMPLATES[index].headline);
    setPrimaryText(AD_HOOK_TEMPLATES[index].primaryText);
  };

  const handleAddKeyword = () => {
    if (newKeywordInput.trim() && !keywords.includes(newKeywordInput.trim())) {
      setKeywords([...keywords, newKeywordInput.trim()]);
      setNewKeywordInput('');
    }
  };

  const handleRemoveKeyword = (kw: string) => {
    setKeywords(keywords.filter((k) => k !== kw));
  };

  const handleTogglePlacement = (p: 'FACEBOOK_FEED' | 'INSTAGRAM_FEED' | 'INSTAGRAM_REELS') => {
    if (placements.includes(p)) {
      if (placements.length > 1) {
        setPlacements(placements.filter((item) => item !== p));
      }
    } else {
      setPlacements([...placements, p]);
    }
  };

  const usdRate = 1520; // NGN per USD
  const dailyBudgetUsd = Math.round(dailyBudgetNgn / usdRate);
  const totalCampaignBudgetNgn = dailyBudgetNgn * durationDays;

  const handleLaunchCampaign = async () => {
    setIsLaunching(true);
    try {
      if (channel === 'GOOGLE_ADS') {
        await createGoogleCampaign({
          campaignName,
          channelType: googleSubChannel,
          targetLocation: selectedRegion,
          radiusKm,
          dailyBudgetNgn,
          dailyBudgetUsd,
          durationDays,
          keywords,
          headline,
          description: primaryText,
          finalUrl: 'https://accesstechnologiesandrenewableenergy.online',
        });
      } else {
        await createMetaCampaign({
          campaignName,
          objective: 'LEAD_GENERATION',
          targetLocation: selectedRegion,
          radiusKm,
          dailyBudgetNgn,
          dailyBudgetUsd,
          durationDays,
          interests: ['Small Business Owners', 'Generator Maintenance', 'Solar Energy'],
          adHeadline: headline,
          adPrimaryText: primaryText,
          callToAction: 'GET_QUOTE',
          placements,
        });
      }

      setLaunchSuccess(true);
      setTimeout(() => {
        setLaunchSuccess(false);
        setIsLaunching(false);
        onCampaignCreated();
        onClose();
      }, 1500);
    } catch (err) {
      console.error(err);
      setIsLaunching(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/85 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-slate-900 border border-slate-800 rounded-3xl max-w-4xl w-full p-6 sm:p-8 shadow-2xl relative text-white space-y-6 my-8 max-h-[90vh] overflow-y-auto"
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-6 right-6 p-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Wizard Header */}
        <div className="space-y-1">
          <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-bold uppercase tracking-wider">
            <Megaphone className="w-3.5 h-3.5" /> Growth OS • Campaign Launch Wizard
          </div>
          <h2 className="text-2xl sm:text-3xl font-black text-white tracking-tight">
            Deploy High-ROI Ad Campaign
          </h2>
          <p className="text-xs sm:text-sm text-slate-400">
            Target blackouts and Band A electricity price spikes in high-cost fuel regions across Nigeria.
          </p>
        </div>

        {/* Channel Selector */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <button
            onClick={() => setChannel('GOOGLE_ADS')}
            className={`p-5 rounded-2xl border transition-all text-left flex items-start gap-4 ${
              channel === 'GOOGLE_ADS'
                ? 'bg-slate-950 border-emerald-500 shadow-lg shadow-emerald-500/10'
                : 'bg-slate-950/60 border-slate-800 hover:border-slate-700'
            }`}
          >
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 font-black text-lg ${
              channel === 'GOOGLE_ADS' ? 'bg-emerald-500 text-slate-950' : 'bg-slate-800 text-slate-400'
            }`}>
              G
            </div>
            <div>
              <div className="font-bold text-white text-sm flex items-center gap-2">
                Google Ads (Search & PMax)
                {channel === 'GOOGLE_ADS' && <CheckCircle2 className="w-4 h-4 text-emerald-400" />}
              </div>
              <p className="text-xs text-slate-400 mt-1">
                Captures buyers searching for "3.5kva solar installer", "inverter cost in Umuahia".
              </p>
            </div>
          </button>

          <button
            onClick={() => setChannel('META_ADS')}
            className={`p-5 rounded-2xl border transition-all text-left flex items-start gap-4 ${
              channel === 'META_ADS'
                ? 'bg-slate-950 border-cyan-500 shadow-lg shadow-cyan-500/10'
                : 'bg-slate-950/60 border-slate-800 hover:border-slate-700'
            }`}
          >
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 font-black text-lg ${
              channel === 'META_ADS' ? 'bg-cyan-500 text-slate-950' : 'bg-slate-800 text-slate-400'
            }`}>
              ∞
            </div>
            <div>
              <div className="font-bold text-white text-sm flex items-center gap-2">
                Meta Ads (Facebook & Instagram)
                {channel === 'META_ADS' && <CheckCircle2 className="w-4 h-4 text-cyan-400" />}
              </div>
              <p className="text-xs text-slate-400 mt-1">
                Target business owners, estate residents, and generator users via Feed & Reels.
              </p>
            </div>
          </button>
        </div>

        {/* High-Intent Audience Segment Presets */}
        <div className="bg-slate-950 p-4 rounded-2xl border border-slate-800 space-y-3">
          <span className="text-xs text-slate-400 font-bold uppercase tracking-wider block flex items-center gap-1.5">
            <Target className="w-3.5 h-3.5 text-emerald-400" /> High-Intent Audience Region Presets:
          </span>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {NIGERIAN_META_TARGETING_PRESETS.map((preset) => (
              <button
                key={preset.id}
                onClick={() => handleApplyPreset(preset.id)}
                className="p-3 rounded-xl bg-slate-900 hover:bg-slate-800 border border-slate-800 text-left transition-all group"
              >
                <div className="font-bold text-xs text-white group-hover:text-emerald-400 flex items-center gap-1">
                  <MapPin className="w-3 h-3 text-emerald-400 shrink-0" />
                  {preset.name.split(' ')[0]}
                </div>
                <div className="text-[11px] text-slate-400 truncate">{preset.region}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Main Form Fields */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left Inputs */}
          <div className="lg:col-span-7 space-y-5">
            {/* Campaign Name */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-slate-300">Campaign Name</label>
              <input
                type="text"
                value={campaignName}
                onChange={(e) => setCampaignName(e.target.value)}
                className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 text-xs text-white focus:outline-none focus:border-emerald-500"
              />
            </div>

            {/* Region & Radius */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-slate-300">Target Region / City</label>
                <input
                  type="text"
                  value={selectedRegion}
                  onChange={(e) => setSelectedRegion(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 text-xs text-white focus:outline-none focus:border-emerald-500"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-slate-300">Target Radius (Km)</label>
                <select
                  value={radiusKm}
                  onChange={(e) => setRadiusKm(Number(e.target.value))}
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 text-xs text-white focus:outline-none focus:border-emerald-500"
                >
                  <option value={10}>10 Km (Strict Metro)</option>
                  <option value={25}>25 Km (City & Suburbs)</option>
                  <option value={50}>50 Km (State-Wide)</option>
                  <option value={100}>100 Km (Regional Cluster)</option>
                </select>
              </div>
            </div>

            {/* Daily Budget Slider */}
            <div className="space-y-2 bg-slate-950 p-4 rounded-2xl border border-slate-800">
              <div className="flex justify-between items-center text-xs">
                <span className="font-semibold text-slate-300 flex items-center gap-1.5">
                  <DollarSign className="w-4 h-4 text-emerald-400" /> Daily Ad Budget:
                </span>
                <span className="font-black text-emerald-400 font-mono text-sm">
                  ₦{dailyBudgetNgn.toLocaleString()}/day (~${dailyBudgetUsd}/day)
                </span>
              </div>
              <input
                type="range"
                min={10000}
                max={250000}
                step={5000}
                value={dailyBudgetNgn}
                onChange={(e) => setDailyBudgetNgn(Number(e.target.value))}
                className="w-full accent-emerald-500 cursor-pointer"
              />
              <div className="flex justify-between text-[11px] text-slate-400 font-mono">
                <span>₦10,000/day</span>
                <span>₦100,000/day</span>
                <span>₦250,000/day</span>
              </div>
            </div>

            {/* Duration Slider */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-slate-300">Campaign Duration</label>
                <select
                  value={durationDays}
                  onChange={(e) => setDurationDays(Number(e.target.value))}
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 text-xs text-white focus:outline-none focus:border-emerald-500"
                >
                  <option value={7}>7 Days Sprint</option>
                  <option value={14}>14 Days Optimal</option>
                  <option value={30}>30 Days Growth</option>
                  <option value={60}>60 Days Scale</option>
                </select>
              </div>

              <div className="bg-slate-950 p-3 rounded-xl border border-slate-800 flex flex-col justify-center">
                <span className="text-[11px] text-slate-400">Total Campaign Commitment</span>
                <span className="text-sm font-black text-cyan-400 font-mono">
                  ₦{totalCampaignBudgetNgn.toLocaleString()}
                </span>
              </div>
            </div>

            {/* Ad Copy Hooks Switcher */}
            <div className="space-y-2">
              <label className="text-xs font-semibold text-slate-300 flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5 text-amber-400" /> Auto-Generated Nigerian Solar Ad Copy Hooks
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {AD_HOOK_TEMPLATES.map((hook, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => handleSelectHook(idx)}
                    className={`p-3 rounded-xl border text-left text-xs transition-all ${
                      selectedHookIndex === idx
                        ? 'bg-slate-950 border-emerald-500 text-white shadow-md'
                        : 'bg-slate-950/60 border-slate-800 text-slate-400 hover:text-white'
                    }`}
                  >
                    <div className="font-bold text-amber-300 text-[11px] mb-1">{hook.title}</div>
                    <div className="line-clamp-2 font-medium">{hook.headline}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Headline & Description */}
            <div className="space-y-3">
              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-300">Ad Headline</label>
                <input
                  type="text"
                  value={headline}
                  onChange={(e) => setHeadline(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 text-xs text-white focus:outline-none focus:border-emerald-500"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-300">Primary Ad Body Text</label>
                <textarea
                  rows={2}
                  value={primaryText}
                  onChange={(e) => setPrimaryText(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-2.5 text-xs text-white focus:outline-none focus:border-emerald-500"
                />
              </div>
            </div>

            {/* Keywords for Google Ads */}
            {channel === 'GOOGLE_ADS' && (
              <div className="space-y-2 bg-slate-950 p-4 rounded-2xl border border-slate-800">
                <label className="text-xs font-semibold text-slate-300 flex items-center justify-between">
                  <span>Target Search Keywords</span>
                  <span className="text-slate-400 text-[11px] font-mono">{keywords.length} Active</span>
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newKeywordInput}
                    onChange={(e) => setNewKeywordInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddKeyword())}
                    placeholder="Add custom keyword..."
                    className="flex-grow bg-slate-900 border border-slate-800 rounded-xl px-3 py-1.5 text-xs text-white focus:outline-none focus:border-emerald-500"
                  />
                  <button
                    type="button"
                    onClick={handleAddKeyword}
                    className="px-3 py-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs"
                  >
                    Add
                  </button>
                </div>
                <div className="flex flex-wrap gap-1.5 pt-2">
                  {keywords.map((kw) => (
                    <span
                      key={kw}
                      className="inline-flex items-center gap-1.5 bg-slate-900 border border-slate-800 text-slate-200 text-[11px] px-2.5 py-1 rounded-lg"
                    >
                      {kw}
                      <button
                        onClick={() => handleRemoveKeyword(kw)}
                        className="text-slate-400 hover:text-rose-400"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Live Preview Card */}
          <div className="lg:col-span-5 space-y-4">
            <span className="text-xs text-slate-400 font-bold uppercase tracking-wider block flex items-center gap-1.5">
              <Eye className="w-3.5 h-3.5 text-cyan-400" /> Real-Time Ad Mockup Preview:
            </span>

            {channel === 'GOOGLE_ADS' ? (
              /* Google Search Ad Preview Card */
              <div className="bg-slate-950 border border-slate-800 rounded-2xl p-5 space-y-3 font-sans shadow-xl">
                <div className="flex items-center gap-2 text-[11px] text-slate-400">
                  <span className="font-extrabold text-slate-200 bg-slate-800 px-1.5 py-0.5 rounded text-[10px]">
                    Sponsored
                  </span>
                  <span>https://accesstechnologiesandrenewableenergy.online</span>
                </div>
                <div className="text-blue-400 font-bold text-sm hover:underline cursor-pointer leading-snug">
                  {headline || 'Beat Band A Tariffs in Umuahia — Switch to Access Tech Solar'}
                </div>
                <p className="text-slate-300 text-xs leading-relaxed">
                  {primaryText || 'Stop Wasting ₦15,000/Day on Petrol. Get 24/7 Uninterrupted Pure Sine Solar.'}
                </p>
                <div className="pt-2 border-t border-slate-900 grid grid-cols-2 gap-2 text-[11px] text-emerald-400 font-medium">
                  <div>✓ 25-Yr Panel Warranty</div>
                  <div>✓ COREN Certified</div>
                  <div>✓ Free Site Audit</div>
                  <div>✓ 0ms Seamless Transfer</div>
                </div>
              </div>
            ) : (
              /* Meta Feed / Reels Preview Card */
              <div className="bg-slate-950 border border-slate-800 rounded-2xl p-5 space-y-3 font-sans shadow-xl">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-gradient-to-tr from-emerald-500 to-teal-400 flex items-center justify-center font-bold text-slate-950 text-xs">
                    AT
                  </div>
                  <div>
                    <div className="font-bold text-white text-xs">Access Tech & Renewable Energy</div>
                    <div className="text-[10px] text-slate-400">Sponsored • {selectedRegion}</div>
                  </div>
                </div>

                <p className="text-xs text-slate-300 leading-relaxed">
                  {primaryText}
                </p>

                {/* Ad Creative Banner Placeholder */}
                <div className="aspect-video bg-gradient-to-br from-slate-900 via-emerald-950 to-slate-900 rounded-xl border border-emerald-500/30 p-4 flex flex-col justify-between relative overflow-hidden">
                  <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-500/20 text-emerald-300 text-[10px] font-black uppercase">
                    <Zap className="w-3 h-3" /> Stop Wasting Petrol Money
                  </div>
                  <div>
                    <div className="text-white font-black text-sm drop-shadow-md">{headline}</div>
                    <div className="text-emerald-400 font-mono text-xs font-bold mt-1">
                      Packages from ₦1,850,000 • 24/7 Electricity
                    </div>
                  </div>
                </div>

                <div className="bg-slate-900 p-3 rounded-xl border border-slate-800 flex justify-between items-center">
                  <div>
                    <div className="text-[10px] text-slate-400 uppercase font-semibold">ACCESSTECH-SOLAR.NG</div>
                    <div className="text-xs font-bold text-white">{headline}</div>
                  </div>
                  <button className="bg-emerald-500 text-slate-950 font-black px-3 py-1.5 rounded-lg text-xs shrink-0">
                    Get Quote
                  </button>
                </div>
              </div>
            )}

            {/* Campaign Summary Box */}
            <div className="bg-slate-950 p-4 rounded-2xl border border-slate-800 space-y-2 text-xs">
              <div className="flex justify-between text-slate-400">
                <span>Estimated Monthly Reach:</span>
                <strong className="text-white">45,000 - 120,000 Impressions</strong>
              </div>
              <div className="flex justify-between text-slate-400">
                <span>Expected High-Intent Leads:</span>
                <strong className="text-emerald-400">35 - 85 Leads / Month</strong>
              </div>
              <div className="flex justify-between text-slate-400">
                <span>Target Cost Per Lead (CPL):</span>
                <strong className="text-amber-400">~₦1,200 - ₦2,500 / Lead</strong>
              </div>
            </div>
          </div>
        </div>

        {/* Launch Campaign Action Footer */}
        <div className="pt-4 border-t border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-xs text-slate-400">
            *Linked to official {channel === 'GOOGLE_ADS' ? 'Google Ads MCC (948-204-8810)' : 'Meta Business Suite (bm_8830194812)'}.
          </div>

          <div className="flex items-center gap-3 w-full sm:w-auto">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-3 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold text-xs transition-colors w-full sm:w-auto"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleLaunchCampaign}
              disabled={isLaunching}
              className="px-6 py-3.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black text-xs uppercase tracking-wider transition-all flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/20 w-full sm:w-auto"
            >
              {isLaunching ? (
                <>Launching Campaign Live...</>
              ) : launchSuccess ? (
                <>Campaign Live & Synchronized! <CheckCircle2 className="w-4 h-4" /></>
              ) : (
                <>Launch {channel === 'GOOGLE_ADS' ? 'Google' : 'Meta'} Campaign <Play className="w-4 h-4 fill-current" /></>
              )}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};
